'use client';

import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { UseSliderResult } from './UiSlider.types';

const EPSILON = 4;

/** Headless horizontal slider: owns the scroll-container ref, tracks whether it
 *  can scroll each way, and steps one item per prev/next. Scroll targets are the
 *  container's direct children, so it works with any item markup. */
export function useSlider<T extends HTMLElement = HTMLElement>(itemCount: number): UseSliderResult<T> {
    const scrollRef = useRef<T | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    function getEls() {
        const el = scrollRef.current;
        if (!el) return null;
        const items = Array.from(el.children) as HTMLElement[];
        if (!items.length) return null;
        return { el, items };
    }

    function scrollByStep(dir: -1 | 1) {
        const els = getEls();
        if (!els) return;

        const { el, items } = els;
        const containerLeft = el.getBoundingClientRect().left;

        if (dir === 1) {
            for (const item of items) {
                const offset = item.getBoundingClientRect().left - containerLeft;
                if (offset > EPSILON) {
                    el.scrollBy({ left: offset, behavior: 'smooth' });
                    return;
                }
            }
            el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
        } else {
            for (let i = items.length - 1; i >= 0; i--) {
                const offset = items[i].getBoundingClientRect().left - containerLeft;
                if (offset < -EPSILON) {
                    el.scrollBy({ left: offset, behavior: 'smooth' });
                    return;
                }
            }
            el.scrollTo({ left: 0, behavior: 'smooth' });
        }
    }

    function scrollPrev() {
        scrollByStep(-1);
    }

    function scrollNext() {
        scrollByStep(1);
    }

    function onKeyDown(event: KeyboardEvent<HTMLElement>) {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            scrollPrev();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            scrollNext();
        }
    }

    useEffect(() => {
        let raf = 0;
        let attached = false;
        let ro: ResizeObserver | null = null;

        function update() {
            const els = getEls();
            if (!els) {
                setCanScrollLeft(false);
                setCanScrollRight(false);
                return;
            }

            const { el, items } = els;
            const last = items[items.length - 1];
            const viewportRect = el.getBoundingClientRect();
            const lastRect = last.getBoundingClientRect();

            setCanScrollLeft(el.scrollLeft > EPSILON);
            setCanScrollRight(lastRect.right > viewportRect.right + EPSILON);
        }

        function schedule() {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(update);
        }

        function attachIfVisible() {
            const el = scrollRef.current;
            if (!el || el.offsetParent === null || attached) return;

            attached = true;
            update();
            el.addEventListener('scroll', schedule, { passive: true });
            ro = new ResizeObserver(schedule);
            ro.observe(el);
        }

        function detach() {
            const el = scrollRef.current;
            if (!el || !attached) return;

            attached = false;
            el.removeEventListener('scroll', schedule);
            ro?.disconnect();
            ro = null;
        }

        // Recompute when the slider becomes visible (e.g. inside a tab/accordion).
        const visRo = new ResizeObserver(() => {
            const el = scrollRef.current;
            if (!el) return;
            if (el.offsetParent === null) detach();
            else attachIfVisible();
        });

        const el0 = scrollRef.current;
        if (el0) visRo.observe(el0);
        attachIfVisible();

        return () => {
            cancelAnimationFrame(raf);
            detach();
            visRo.disconnect();
        };
    }, [itemCount]);

    return { scrollRef, canScrollLeft, canScrollRight, scrollPrev, scrollNext, onKeyDown };
}
